<script setup lang="ts" generic="T extends Array<Record<string, any>>">
const windowScroll = useWindowScroll();
const device = useDevice();

const props = defineProps<{ items: T }>();
const parent = useTemplateRef('parent');
const imgs = useTemplateRef('imgs');
const offset = useElementBounding(parent);
const activeIndex = ref(0);
const imgRotate = ref(0);
const bottomIndicate = 122;
const rotationIndicate = 90;

const startFixing = computed(() => {
    if (
        !windowScroll.y
        || !device.isDesktop.value
        || !imgs.value 
        || !offset.bottom.value 
        || !offset.top.value
    ) return;

    return offset.bottom.value > imgs.value.offsetHeight + bottomIndicate
        && offset.top.value < 110;
});

const endFixing = computed(() => {
    if (
        !device.isDesktop.value
        || !imgs.value 
        || !offset.bottom.value
    ) return;

    return offset.bottom.value < imgs.value.offsetHeight + bottomIndicate;
});


function setActiveIndex(index: number) {
    activeIndex.value = index;
};

watch(windowScroll.y, (newVal, oldVal) => {
    if (!startFixing.value || endFixing.value) return;

    const delta = newVal - (oldVal ?? newVal);
    imgRotate.value += delta * (rotationIndicate / 400);

    if (imgRotate.value >= rotationIndicate) {
        if (activeIndex.value < (props.items.length - 1)) {
            setActiveIndex(activeIndex.value + 1);
            imgRotate.value = -rotationIndicate;
        } else {
            imgRotate.value = rotationIndicate;
        }
    } else if (imgRotate.value <= -rotationIndicate) {
        if (activeIndex.value > 0) {
            setActiveIndex(activeIndex.value - 1);
            imgRotate.value = rotationIndicate;
        } else {
            imgRotate.value = -rotationIndicate;
        }
    }
});

watch(startFixing, () => { imgRotate.value = 0 });

watch(endFixing, () => { imgRotate.value = 0 });
</script>

<template>
    <div ref="parent" class="w-full">
        <div class="flex flex-col gap-75 items-center">
            <div
                v-for="item, index in props.items"
                :key="item.title"
                :id="item.title"
                class="w-full flex items-center gap-12 py-6 px-8 bg-transparent rounded-3xl mt-10"
                :class="index === props.items.length - 1 && 'pb-15'"
            >
                <div class="w-[45%]">
                    <h3>{{ item.title }}</h3>
                    <p class="text-body1 text-gray-400 mt-3">{{ item.desc }}</p>
                    <div class="w-full flex flex-col gap-9 mt-9">
                    <div
                        v-for="group in item.groups"
                        :key="group.title"
                        class="w-full"
                    >
                        <div class="w-full flex items-center gap-4">
                        <Icon name="solar:check-circle-bold" size="24" class="text-secondary-600" />
                        <h4 class="text-gray-600/80">{{ group.title }}</h4>
                        </div>
                        <p class="text-body2 text-gray-400 mt-4 mr-10">{{ group.desc }}</p>
                    </div>
                    </div>
                </div>
            </div>
            <div 
                ref="imgs" 
                class="absolute top-60 inset-x-0"
                :class="[
                    startFixing && !endFixing && 'fixed top-32!',
                    endFixing && 'top-auto! bottom-8!'
                ]"
            >
                <div class="container flex justify-end">
                    <div class="w-[40%] perspective-distant">
                        <div class="img-wrapper">
                            <img
                                :src="props.items[activeIndex]?.img"
                                :alt="props.items[activeIndex]?.title ?? 'امکانات'"
                                class="w-full"
                            />
                        </div>
                    </div>
                </div>   
            </div>
        </div>
    </div>
</template>

<style scoped>
@reference "~/assets/css/main.css";

.img-wrapper {
    --i: v-bind("imgRotate + 'deg'");
    transform: rotateX(var(--i));
    @apply
    w-full bg-primary-50 border-2 border-gray-300/40 rounded-3xl p-8 relative overflow-hidden
}
</style>